import { useContext, useEffect, useRef } from 'react';

import { PageShortcutsContext } from './PageShortcutsContext';
import { IPageAction } from './types';

/**
 * Register a page's contextual actions for the lifetime of the component.
 *
 * The current list is kept in a ref (refreshed every render) and exposed to the
 * provider through a STABLE getter registered once on mount, so re-renders never
 * churn the registry and the effect deps stay exhaustive-deps clean. The provider
 * reads the getter live, so `run` closures and `when` flags are always current.
 */
export const usePageActions = (actions: IPageAction[]): void => {
  const { register, unregister } = useContext(PageShortcutsContext);

  const actionsRef = useRef<IPageAction[]>(actions);
  actionsRef.current = actions;

  // One stable id + getter per mounted consumer.
  const idRef = useRef<symbol | null>(null);
  if (idRef.current === null) {
    idRef.current = Symbol('pageActions');
  }
  const getterRef = useRef<() => IPageAction[]>(() => actionsRef.current);

  useEffect(() => {
    const id = idRef.current as symbol;
    register(id, getterRef.current);
    return () => unregister(id);
  }, [register, unregister]);
};

/** @deprecated alias of `usePageActions`. */
export const usePageShortcuts = usePageActions;

/**
 * Live list of the currently-active contextual actions (`when !== false`), for
 * the palette section + cheat sheet. Read on every render: `useContext`
 * re-renders the consumer whenever the provider's memoized value changes (its
 * `version` bumps on each register/unregister), and the palette re-renders when
 * it opens — so the snapshot is always current at display time, including live
 * `when` flags.
 */
export const useActivePageActions = (): IPageAction[] => {
  const { getShortcuts } = useContext(PageShortcutsContext);
  return getShortcuts().filter((action) => action.when !== false);
};

/** @deprecated alias of `useActivePageActions`. */
export const useActivePageShortcuts = useActivePageActions;
